/**
 * Merkle Tree Builder
 * 
 * Efficiently builds merkle trees for data integrity verification
 * and lightweight delta sync detection using SHA-256 hashing.
 * 
 * Features:
 * - Memory-efficient tree construction
 * - Incremental hash computation
 * - Delta detection via tree comparison
 * - Persistence layer for offline operation
 */

import { createLogger } from '../utils/logger';

const logger = createLogger('MerkleTree');

/**
 * Browser-compatible SHA-256 hash function using Web Crypto API
 */
async function sha256(data: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Browser-compatible byte length calculation
 */
function byteLength(str: string): number {
  return new Blob([str]).size;
}

export interface HashedRecord {
  id: string;
  data: any;
  hash: string;              // SHA-256 of serialized data
  timestamp: Date;
  size: number;              // Bytes
}

export interface MerkleNode {
  hash: string;              // SHA-256 of children
  level: number;             // 0 = leaf, 1+ = branch
  children?: MerkleNode[];   // Left and right children
  records?: HashedRecord[];  // For leaf nodes only
  timestamp: Date;
  recordCount: number;
}

export interface MerkleTree {
  root: MerkleNode;
  leaves: MerkleNode[];
  table: string;
  recordCount: number;
  totalSize: number;         // Total bytes
  depth: number;             // Tree depth
  generatedAt: Date;
  hash: string;              // Root hash (convenience)
}

export interface TreeComparison {
  rootsMatch: boolean;
  changedRecordIds: string[];
  changedCount: number;
  similarityPercent: number;
}

/**
 * Merkle Tree Builder
 * Constructs balanced merkle trees from datasets
 */
export class MerkleTreeBuilder {
  /**
   * Hash a record using SHA-256
   */
  static async hashRecord(record: HashedRecord): Promise<string> {
    const data = JSON.stringify(record.data);
    return await sha256(data);
  }

  /**
   * Hash two child hashes to create parent
   */
  static async hashPair(left: string, right: string): Promise<string> {
    const combined = left + right;
    return await sha256(combined);
  }

  /**
   * Build complete merkle tree for dataset
   */
  static async buildTree(
    records: any[],
    table: string
  ): Promise<MerkleTree> {
    logger.info('Building merkle tree', { table, recordCount: records.length });

    if (records.length === 0) {
      throw new Error(`Cannot build merkle tree for ${table}: no records`);
    }

    // Step 1: Sort records by ID for consistent hashing
    const sorted = records.sort((a, b) => a.id.localeCompare(b.id));

    // Step 2: Create leaf nodes with hashes
    const leaves: MerkleNode[] = await Promise.all(sorted.map(async (record) => {
      const data = JSON.stringify(record);
      const hash = await sha256(data);
      const size = byteLength(data);

      return {
        hash,
        level: 0,
        records: [{ id: record.id, data: record, hash, timestamp: new Date(), size }],
        timestamp: new Date(),
        recordCount: 1,
      };
    }));

    // Step 3: Build tree bottom-up (recursive pairing)
    let currentLevel = leaves;
    let level = 1;
    const startTime = performance.now();

    while (currentLevel.length > 1) {
      const nextLevel: MerkleNode[] = [];

      for (let i = 0; i < currentLevel.length; i += 2) {
        const left = currentLevel[i];
        const right = currentLevel[i + 1] || left; // Duplicate if odd

        const parentHash = await this.hashPair(left.hash, right.hash);
        const parent: MerkleNode = {
          hash: parentHash,
          level,
          children: [left, right],
          timestamp: new Date(),
          recordCount: left.recordCount + right.recordCount,
        };

        nextLevel.push(parent);
      }

      currentLevel = nextLevel;
      level++;
    }

    const root = currentLevel[0];
    const totalSize = sorted.reduce((sum, r) => sum + byteLength(JSON.stringify(r)), 0);
    const buildTime = performance.now() - startTime;

    logger.info('Merkle tree built', {
      table,
      rootHashPreview: root.hash.substring(0, 16),
      leaves: leaves.length,
      depth: level,
      sizeKB: (totalSize / 1024).toFixed(2),
      buildTimeMs: buildTime.toFixed(2)
    });

    return {
      root,
      leaves,
      table,
      recordCount: records.length,
      totalSize,
      depth: level,
      generatedAt: new Date(),
      hash: root.hash,
    };
  }

  /**
   * Get branch hashes at specific tree level
   * Used for phase 2 of sync protocol
   */
  static getBranchHashes(tree: MerkleTree, level: number): string[] {
    const hashes: string[] = [];

    const traverse = (node: MerkleNode) => {
      if (node.level === level) {
        hashes.push(node.hash);
      } else if (node.children && node.level < level) {
        node.children.forEach(traverse);
      }
    };

    traverse(tree.root);
    return hashes;
  }

  /**
   * Get all leaf hashes (for full verification)
   */
  static getLeafHashes(tree: MerkleTree): string[] {
    return tree.leaves.map((leaf) => leaf.hash);
  }

  /**
   * Get all record IDs in tree (for reference)
   */
  static getRecordIds(tree: MerkleTree): string[] {
    return tree.leaves.reduce((ids: string[], leaf) => {
      if (leaf.records) {
        leaf.records.forEach((record) => {
          ids.push(record.id);
        });
      }
      return ids;
    }, []);
  }

  /**
   * Estimate tree size in bytes (for bandwidth planning)
   */
  static estimateTreeMemory(recordCount: number): number {
    // Rough estimate: Each node ~200 bytes overhead
    const nodeCount = recordCount + Math.ceil(recordCount / 2);
    return nodeCount * 200;
  }
}

/**
 * Tree Comparison & Delta Detection
 */
export class TreeComparator {
  /**
   * Compare two merkle roots
   */
  static areRootsEqual(hash1: string, hash2: string): boolean {
    return hash1 === hash2;
  }

  /**
   * Deep comparison to find changed records
   */
  static compareRecursive(
    localNode: MerkleNode,
    remoteHash: string,
    changedIds: Set<string>
  ): void {
    // Roots match - subtree unchanged
    if (localNode.hash === remoteHash) {
      return;
    }

    // Leaf node - record changed
    if (localNode.level === 0) {
      if (localNode.records) {
        localNode.records.forEach((record) => {
          changedIds.add(record.id);
        });
      }
      return;
    }

    // Branch node - recurse
    if (localNode.children) {
      localNode.children.forEach((child) => {
        // In real implementation, would need remote child hashes
        // For now, mark all records in changed subtree
        this.collectRecordIds(child, changedIds);
      });
    }
  }

  /**
   * Collect all record IDs in subtree
   */
  private static collectRecordIds(node: MerkleNode, ids: Set<string>): void {
    if (node.level === 0 && node.records) {
      node.records.forEach((record) => {
        ids.add(record.id);
      });
    } else if (node.children) {
      node.children.forEach((child) => {
        this.collectRecordIds(child, ids);
      });
    }
  }

  /**
   * Compare two complete trees
   */
  static compareTrees(
    localTree: MerkleTree,
    remoteTree: MerkleTree
  ): TreeComparison {
    logger.info('Comparing merkle trees', { table: localTree.table });

    const rootsMatch = this.areRootsEqual(localTree.hash, remoteTree.hash);

    if (rootsMatch) {
      logger.info('Trees match - data identical');
      return {
        rootsMatch: true,
        changedRecordIds: [],
        changedCount: 0,
        similarityPercent: 100,
      };
    }

    logger.warn('Trees differ - analyzing deltas');

    // For accurate delta detection, would need to walk both trees
    // This is simplified version - in production, server provides remote hashes
    const changedIds = new Set<string>();
    this.compareRecursive(localTree.root, remoteTree.hash, changedIds);

    const changedRecordIds = Array.from(changedIds);
    const similarityPercent = Math.round(
      ((localTree.recordCount - changedRecordIds.length) / localTree.recordCount) * 100
    );

    logger.info('Delta analysis', {
      changedRecords: changedRecordIds.length,
      similarityPercent
    });

    return {
      rootsMatch: false,
      changedRecordIds,
      changedCount: changedRecordIds.length,
      similarityPercent,
    };
  }
}

/**
 * Tree Persistence & Caching
 * Avoids rebuilding trees on every sync
 */
export interface CachedTree {
  tree: MerkleTree;
  recordHashes: Map<string, string>;  // id -> hash mapping
  cachedAt: Date;
  expiresAt: Date;
}

export class TreeCache {
  private cache: Map<string, CachedTree> = new Map();
  private cacheExpiry = 5 * 60 * 1000; // 5 minutes

  /**
   * Get cached tree if valid
   */
  get(table: string): MerkleTree | null {
    const cached = this.cache.get(table);

    if (!cached) {
      return null;
    }

    // Check if expired
    if (new Date() > cached.expiresAt) {
      this.cache.delete(table);
      logger.info('Expired cache', { table });
      return null;
    }

    logger.info('Using cached merkle tree', { table });
    return cached.tree;
  }

  /**
   * Store tree in cache
   */
  set(table: string, tree: MerkleTree): void {
    const recordHashes = new Map<string, string>();

    tree.leaves.forEach((leaf) => {
      if (leaf.records) {
        leaf.records.forEach((record) => {
          recordHashes.set(record.id, record.hash);
        });
      }
    });

    this.cache.set(table, {
      tree,
      recordHashes,
      cachedAt: new Date(),
      expiresAt: new Date(Date.now() + this.cacheExpiry),
    });

    logger.info('Cached merkle tree', { table });
  }

  /**
   * Invalidate cache (after sync)
   */
  invalidate(table: string): void {
    this.cache.delete(table);
    logger.info('Cleared cache', { table });
  }

  /**
   * Clear all caches
   */
  clear(): void {
    this.cache.clear();
    logger.info('Cleared all tree caches');
  }

  /**
   * Get cache size (bytes)
   */
  getSize(): number {
    let size = 0;
    this.cache.forEach((cached) => {
      size += cached.tree.totalSize;
    });
    return size;
  }
}

export const treeCache = new TreeCache();
